import { ValueRadarGroup, ValueRadarHorse, useValueRadarBoard, useValueRadarRecord } from '../../queries/multis';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';

const GROUP_LABEL: Record<ValueRadarGroup, string> = {
  keep_race: 'Keep · maiden/class',
  keep_place: 'Keep · place tip',
  keep: 'Keep',
  avoid: 'Avoid',
};
const GROUP_VARIANT: Record<ValueRadarGroup, 'success' | 'primary' | 'default' | 'outline'> = {
  keep_race: 'success',
  keep_place: 'primary',
  keep: 'default',
  avoid: 'outline',
};
const pct = (v: number | string | null) => (v == null ? '-' : `${Math.round(Number(v) * 100)}%`);
const money = (v: number | string | null) => (v == null ? '-' : `$${Number(v).toFixed(2)}`);

function resultText(h: ValueRadarHorse): string {
  if (h.status === 'void') return 'Scratched';
  if (h.status === 'pending') return 'Pending';
  return `${h.status === 'won' ? 'Won' : 'Lost'} (${h.finish_position})`;
}

/**
 * The day's Value Radar board: every Value Radar horse with its group (from the 2026-10-10 study) and a
 * proposed position to build your own multi from. Rule-based, written by the Multi Builder at 09:00.
 * Below it, each group's forward record over the last 30 days.
 */
export function ValueRadarBoard({ date }: { date: string }) {
  const { data: horses = [], isLoading } = useValueRadarBoard(date);
  const { data: record = [] } = useValueRadarRecord(30);

  return (
    <Card aria-label="Build your own: Value Radar">
      <CardHeader>
        <CardTitle className="text-base">Build your own: Value Radar</CardTitle>
        <p className="text-sm text-muted-foreground">
          Every Value Radar horse today with a proposed position: the tightest Top N with a 60%+ chance. Avoid = assessed within $1 of our
          shortest in the race (but not it), or first up - they have finished well under their price. Chances are market-based; prices are
          estimates.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Spinner />
        ) : horses.length === 0 ? (
          <p className="text-sm text-muted-foreground">No Value Radar board for this day.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="py-1 pr-2">Race</th>
                  <th className="py-1 pr-2">Horse</th>
                  <th className="py-1 pr-2">Group</th>
                  <th className="py-1 pr-2">Proposed</th>
                  <th className="py-1 pr-2">Chance</th>
                  <th className="py-1 pr-2">Est.</th>
                  <th className="py-1 pr-2">T3 / T4 / T5</th>
                  <th className="py-1">Result</th>
                </tr>
              </thead>
              <tbody>
                {horses.map((h) => (
                  <tr key={h.id} className={`border-t border-border ${h.vr_group === 'avoid' ? 'opacity-50' : ''}`}>
                    <td className="py-1.5 pr-2">
                      {h.track_name} R{h.race_number}
                    </td>
                    <td className="py-1.5 pr-2">
                      #{h.tab_number} {h.horse_name}
                      <span className="block text-xs text-muted-foreground">
                        {h.pick}
                        {h.tip_odds != null ? ` · tipped at ${money(h.tip_odds)}` : ''}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2">
                      <Badge variant={GROUP_VARIANT[h.vr_group]} title={h.reason ?? undefined}>
                        {GROUP_LABEL[h.vr_group]}
                      </Badge>
                      {h.vr_group === 'avoid' && h.reason && <span className="block text-xs text-muted-foreground">{h.reason}</span>}
                    </td>
                    <td className="py-1.5 pr-2">{h.proposed_n ? `Top ${h.proposed_n} of ${h.field_size}` : '-'}</td>
                    <td className="py-1.5 pr-2">{pct(h.chance)}</td>
                    <td className="py-1.5 pr-2">{money(h.est_price)}</td>
                    <td className="py-1.5 pr-2 text-muted-foreground">
                      {pct(h.chance_top3)} / {pct(h.chance_top4)} / {pct(h.chance_top5)}
                    </td>
                    <td className="py-1.5">{resultText(h)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {record.length > 0 && (
          <div aria-label="Value Radar record, last 30 days">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Record, last 30 days (settled)</p>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-1 pr-2">Group</th>
                  <th className="py-1 pr-2">Runners</th>
                  <th className="py-1 pr-2">Top 3</th>
                  <th className="py-1 pr-2">Top 4</th>
                  <th className="py-1 pr-2">Top 5</th>
                  <th className="py-1">Proposed position landed</th>
                </tr>
              </thead>
              <tbody>
                {record.map((r) => (
                  <tr key={r.vr_group} className="border-t border-border">
                    <td className="py-1 pr-2">{GROUP_LABEL[r.vr_group]}</td>
                    <td className="py-1 pr-2">{r.runners}</td>
                    <td className="py-1 pr-2">{r.top3_pct}%</td>
                    <td className="py-1 pr-2">{r.top4_pct}%</td>
                    <td className="py-1 pr-2">{r.top5_pct}%</td>
                    <td className="py-1">
                      {r.proposed_hit_pct}% <span className="text-muted-foreground">(expected {r.proposed_expected_pct}%)</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
